/**
 * Opaque handle encoding for bindable symbols.
 *
 * A symbolRef is a short, deterministic string that names one specific
 * binding source currently valid in the screen (a cascader leaf the human
 * picker would show). The mcp-chat-server returns these in the `bindables`
 * manifest attached to `ui_render` responses; the LLM then hands them back
 * in `ui_bind` calls without having to construct a full ISymbol.
 *
 * Grammar: `<kind>:<arg1>[:<arg2>...]`, colon-separated. Identifiers on the
 * right-hand side must not contain the separator character.
 */

export type CardinalityConversion = "first" | "last" | "selected";

export interface SymbolRefDefault {
  kind: "default";
  name: string;
}

export interface SymbolRefScreenInput {
  kind: "screenInput";
  name: string;
}

export interface SymbolRefDi {
  kind: "di";
  kuikaId: string;
  propName: string;
  conversion?: CardinalityConversion;
}

export interface SymbolRefActionResult {
  kind: "actionResult";
  resultActionId: string;
}

export interface SymbolRefActionField {
  kind: "actionField";
  resultActionId: string;
  fieldPath: string;
}

export interface SymbolRefActionDef {
  kind: "actionDef";
  actionDefinitionId: string;
}

export interface SymbolRefCurrent {
  kind: "current";
  fieldPath: string;
}

export interface SymbolRefBag {
  kind: "bag";
  bagName: string;
}

export type SymbolRefParsed =
  | SymbolRefDefault
  | SymbolRefScreenInput
  | SymbolRefDi
  | SymbolRefActionResult
  | SymbolRefActionField
  | SymbolRefActionDef
  | SymbolRefCurrent
  | SymbolRefBag;

const SEPARATOR = ":";

const VALID_CONVERSIONS: readonly CardinalityConversion[] = ["first", "last", "selected"];

const ensureNonEmpty = (field: string, value: string): void => {
  if (value === "") {
    throw new Error(`symbolRef: ${field} is empty`);
  }
};

const ensureNoSeparator = (field: string, value: string): void => {
  if (value.includes(SEPARATOR)) {
    throw new Error(`symbolRef: ${field} contains separator "${SEPARATOR}": ${value}`);
  }
};

const ensureFieldPath = (field: string, value: string): void => {
  ensureNonEmpty(field, value);
  ensureNoSeparator(field, value);
};

export const encodeSymbolRef = (parsed: SymbolRefParsed): string => {
  switch (parsed.kind) {
    case "default": {
      ensureNonEmpty("name", parsed.name);
      ensureNoSeparator("name", parsed.name);
      return `default${SEPARATOR}${parsed.name}`;
    }
    case "screenInput": {
      ensureNonEmpty("name", parsed.name);
      ensureNoSeparator("name", parsed.name);
      return `screenInput${SEPARATOR}${parsed.name}`;
    }
    case "di": {
      ensureNonEmpty("kuikaId", parsed.kuikaId);
      ensureNoSeparator("kuikaId", parsed.kuikaId);
      ensureNonEmpty("propName", parsed.propName);
      ensureNoSeparator("propName", parsed.propName);
      const base = `di${SEPARATOR}${parsed.kuikaId}${SEPARATOR}${parsed.propName}`;
      if (parsed.conversion) {
        return `${base}${SEPARATOR}${parsed.conversion}`;
      }
      return base;
    }
    case "actionResult": {
      ensureNonEmpty("resultActionId", parsed.resultActionId);
      ensureNoSeparator("resultActionId", parsed.resultActionId);
      return `actionResult${SEPARATOR}${parsed.resultActionId}`;
    }
    case "actionField": {
      ensureNonEmpty("resultActionId", parsed.resultActionId);
      ensureNoSeparator("resultActionId", parsed.resultActionId);
      ensureFieldPath("fieldPath", parsed.fieldPath);
      return `actionField${SEPARATOR}${parsed.resultActionId}${SEPARATOR}${parsed.fieldPath}`;
    }
    case "actionDef": {
      ensureNonEmpty("actionDefinitionId", parsed.actionDefinitionId);
      ensureNoSeparator("actionDefinitionId", parsed.actionDefinitionId);
      return `actionDef${SEPARATOR}${parsed.actionDefinitionId}`;
    }
    case "current": {
      ensureFieldPath("fieldPath", parsed.fieldPath);
      return `current${SEPARATOR}${parsed.fieldPath}`;
    }
    case "bag": {
      ensureNonEmpty("bagName", parsed.bagName);
      ensureNoSeparator("bagName", parsed.bagName);
      return `bag${SEPARATOR}${parsed.bagName}`;
    }
  }
};

const splitRef = (ref: string): string[] => ref.split(SEPARATOR);

const requireArg = (parts: string[], index: number, field: string): string => {
  const value = parts[index];
  if (value === undefined || value === "") {
    throw new Error(`symbolRef: missing ${field}`);
  }
  return value;
};

const isValidConversion = (value: string): value is CardinalityConversion =>
  VALID_CONVERSIONS.includes(value as CardinalityConversion);

export const decodeSymbolRef = (ref: string): SymbolRefParsed => {
  if (ref === "") {
    throw new Error("symbolRef: empty ref");
  }
  const parts = splitRef(ref);
  const kind = parts[0];
  switch (kind) {
    case "default": {
      const name = requireArg(parts, 1, "name");
      return { kind: "default", name };
    }
    case "screenInput": {
      const name = requireArg(parts, 1, "name");
      return { kind: "screenInput", name };
    }
    case "di": {
      const kuikaId = requireArg(parts, 1, "di kuikaId");
      const propName = requireArg(parts, 2, "di propName");
      if (parts.length === 3) {
        return { kind: "di", kuikaId, propName };
      }
      if (parts.length === 4) {
        const conversion = parts[3];
        if (!isValidConversion(conversion)) {
          throw new Error(`symbolRef: invalid di conversion "${conversion}"`);
        }
        return { kind: "di", kuikaId, propName, conversion };
      }
      throw new Error(`symbolRef: di expects 3 or 4 parts, got ${parts.length}`);
    }
    case "actionResult": {
      const resultActionId = requireArg(parts, 1, "resultActionId");
      // 2 parts = whole-result binding; 3+ parts = unambiguous field
      // selector, so rewrite to `actionField:` rather than drop the
      // extra segments.
      if (parts.length >= 3) {
        const fieldPath = parts.slice(2).join(SEPARATOR);
        ensureFieldPath("fieldPath", fieldPath);
        return { kind: "actionField", resultActionId, fieldPath };
      }
      return { kind: "actionResult", resultActionId };
    }
    case "actionField": {
      const resultActionId = requireArg(parts, 1, "resultActionId");
      const fieldPath = requireArg(parts, 2, "fieldPath");
      return { kind: "actionField", resultActionId, fieldPath };
    }
    case "actionDef": {
      const actionDefinitionId = requireArg(parts, 1, "actionDefinitionId");
      return { kind: "actionDef", actionDefinitionId };
    }
    case "current": {
      const fieldPath = requireArg(parts, 1, "fieldPath");
      return { kind: "current", fieldPath };
    }
    case "bag": {
      const bagName = requireArg(parts, 1, "bagName");
      return { kind: "bag", bagName };
    }
    default:
      throw new Error(`symbolRef: unknown kind "${kind ?? ""}"`);
  }
};
